import { useEffect, useMemo, useState } from 'react';
import { history, useIntl } from 'umi';
import { notification, PagingQuery, TableColumn } from 'tea-component';
import { buriedPoint } from '@home/utils/buriedPoint';
import { SYSTEM_NAME } from '@dbl/core/global';
import { updateUrlParams } from '@/pages/datalab/utils/utils';
import { LANGUAGE_EN } from '@home/_config/config';
import { deleteYaml, getModelOptions, getModelTable, getYaml, publishYaml, testYaml, unpublishYaml } from '../service';
import type { IEditablePerson, IModelRecord, IPageInfo } from '../types';
import { getColumns } from '../components/getColumns';
import { SortableColumn } from 'tea-component/lib/table/addons/sortable';
import { YAML_TEST_RESULT_NOTIFICATION_CLASS } from '../constant';
import { createCacheManager } from '../utils/cacheManager';
import { showYamlTestFailureFeedback } from '../utils/showYamlTestFailureFeedback';
import { useQueryParams } from './useQueryParams';

type TQuickValue = {
  creator_name: string[];
  deployment_stage: string[];
  published_version: string[];
};

export interface IAccessInfo {
  id: number | string;
  editors: Array<{ userName: string; userId: string; isView: boolean; isDevelop: boolean; }>;
  creatorId: string;
}

export const useModelManagementCenter = (props: any) => {
  const { gameCode, userId } = props || {};
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  // 使用自定义 hook 解析查询参数
  const queryParams = useQueryParams<{
    keywords: string;
    sort: any;
    pageInfo: any;
    quickValue: any;
  }>(['keywords', 'sort', 'pageInfo', 'quickValue']);

  const keywordsFromQuery = queryParams.keywords || '';
  const sortFromQuery = queryParams.sort;
  const pageInfoFromQuery = queryParams.pageInfo;
  const quickValueFromQuery = queryParams.quickValue;

  const [searchWords, setSearchWords] = useState<string>(keywordsFromQuery);
  const [searchValue, setSearchValue] = useState<string>(keywordsFromQuery);

  const [filterData, setFilterData] = useState({
    creator_names: [] as string[],
    deployment_stages: [] as string[],
    published_versions: [] as string[],
  });

  const [sort, setSort] = useState<any>(sortFromQuery || { by: 'updated_at', order: 'desc' });
  const [quickValue, setQuickValue] = useState<TQuickValue>(
    quickValueFromQuery || { creator_name: [], deployment_stage: [], published_version: [] }
  );
  const [pageInfo, setPageInfo] = useState<IPageInfo>(pageInfoFromQuery || { pageSize: 10, pageIndex: 1 });

  const [records, setRecords] = useState<IModelRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [error, setError] = useState<boolean>(false);

  const [detailVisible, setDetailVisible] = useState<boolean>(false);
  const [detailData, setDetailData] = useState<any>({});
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailError, setDetailError] = useState<boolean>(false);

  const [accessInfo, setAccessInfo] = useState<IAccessInfo>({ id: -1, editors: [], creatorId: '' });

  const [deleteInfo, setDeleteInfo] = useState<IModelRecord | null>(null);
  const [deleteVisible, setDeleteVisible] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  // 操作区详情
  const [actionsDetail, setActionsDetail] = useState<IModelRecord | null>(null);
  // loading态
  const [testLoading, setTestLoading] = useState<boolean>(false);
  const [publishLoading, setPublishLoading] = useState<boolean>(false);
  const [unpublishLoading, setUnpublishLoading] = useState<boolean>(false);

  const nameMap: Record<string, string> = {
    draft: formatMessage({ id: 'dbl_model_management_draft' }),
    tested: formatMessage({ id: 'dbl_model_management_tested' }),
    published: formatMessage({ id: 'dbl_model_management_published' }),
  };

  const getTableData = (
    nextPageInfo: IPageInfo,
    sortInfo: { by: string; order: 'asc' | 'desc'; },
    quickInfo: TQuickValue,
    keywords = searchWords
  ) => {
    setRecords([]);
    setTotal(0);
    setLoading(true);
    setPageInfo(nextPageInfo);
    setSort(sortInfo);
    setQuickValue(quickInfo);
    updateUrlParams({ pageInfo: nextPageInfo, sort: sortInfo, keywords, quickValue: quickInfo });

    const params = {
      page: nextPageInfo.pageIndex || 1,
      page_size: nextPageInfo.pageSize || 10,
      game_code: gameCode,
      search: keywords,
      deployment_stages: quickInfo?.deployment_stage || [],
      creator_names: quickInfo?.creator_name || [],
      published_versions: quickInfo?.published_version || [],
      sort: [
        {
          key: sortInfo?.by || 'updated_at',
          value: sortInfo?.order || 'desc',
        },
      ],
    };
    return getModelTable(params)
      .then((res: any) => {
        setRecords((res?.list || []) as IModelRecord[]);
        setTotal(res?.total_count || 0);
        setLoading(false);
        setError(false);
      })
      .catch((err: unknown) => {
        setLoading(false);
        setError(true);
        throw err;
      });
  };

  const getFilterData = (search = searchWords) => {
    const params = { game_code: gameCode, search };
    return getModelOptions(params)
      .then((res: any) => {
        setFilterData(res || { creator_names: [], deployment_stages: [], published_versions: [] });
      })
      .catch((err: unknown) => {
        setFilterData({
          creator_names: [],
          deployment_stages: [],
          published_versions: [],
        });
        throw err;
      });
  };

  /** 列表与筛选项并行刷新：任一方失败仍等另一方结束，再统一提示（避免 Promise.all 单方失败导致另一方结果未落地却被忽略） */
  const notifyParallelRefreshFailed = (results: PromiseSettledResult<unknown>[]): void => {
    if (results.some((r) => r.status === 'rejected')) {
      notification.warning({
        className: YAML_TEST_RESULT_NOTIFICATION_CLASS,
        description: formatMessage({ id: 'dbl_model_management_list_refresh_failed' }),
      });
    }
  };

  const refreshTableAndFilters = (opts?: { pageInfo?: IPageInfo; keywords?: string; }) => {
    const pi = opts?.pageInfo ?? pageInfo;
    const kw = opts?.keywords ?? searchWords;
    return Promise.allSettled([
      getTableData(pi, sort, quickValue, kw),
      getFilterData(kw),
    ]).then(notifyParallelRefreshFailed);
  };

  const handleClearSearch = () => {
    const resetPage = { pageSize: pageInfo?.pageSize, pageIndex: 1 };
    void Promise.allSettled([
      getTableData(resetPage, sort, quickValue, ''),
      getFilterData(''),
    ]).then(notifyParallelRefreshFailed);
  };

  /** 搜索框提交关键字：重置为第一页并清空快捷筛选项，并行拉表与筛选项 */
  const handleSearchWithKeyword = (kw: string): void => {
    void Promise.allSettled([
      getTableData(
        { pageSize: pageInfo?.pageSize, pageIndex: 1 },
        { by: 'updated_at', order: 'desc' },
        {
          creator_name: [],
          deployment_stage: [],
          published_version: [],
        },
        kw,
      ),
      getFilterData(kw),
    ]).then(notifyParallelRefreshFailed);
  };

  useEffect(() => {
    buriedPoint({
      pageId: '602001',
      pageName: '模型管理中心',
      pagePath: '/DataManagement/ModelManagementCenter',
      extInfo: '{"pageName": "Model Management Center", "pageNameCn": "模型管理中心", "id": ""}',
      uid: gameCode,
      typeId: 'datalab_pc_v2',
      menuId: '0',
    });
    void Promise.allSettled([
      getTableData(pageInfo, sort, quickValue),
      getFilterData(),
    ]).then(notifyParallelRefreshFailed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameCode]);

  const handleOnClickCreateSearch = (key: keyof TQuickValue, value: string) => {
    const newQuickValue = { ...quickValue, [key]: value };
    void getTableData({ pageSize: pageInfo?.pageSize, pageIndex: 1 }, sort, newQuickValue).catch(() => {
      notification.warning({
        className: YAML_TEST_RESULT_NOTIFICATION_CLASS,
        description: formatMessage({ id: 'dbl_model_management_list_refresh_failed' }),
      });
    });
  };

  const { creatorsFilterData, deploymentStageFilterData, publishVersionFilterData } = useMemo(() => {
    const { creator_names: creatorsFilter, deployment_stages: deploymentStageFilter, published_versions: publishVersionFilter } = filterData;
    const getOptions = (data: string[]) => data?.map((item) => ({ value: item, text: item || '-' }));
    return {
      deploymentStageFilterData: getOptions(deploymentStageFilter)?.map((item) => ({ value: item?.value, text: nameMap?.[item?.value] || '-' })),
      publishVersionFilterData: getOptions(publishVersionFilter),
      creatorsFilterData: getOptions(creatorsFilter),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData, isEn]);

  // model detail
  const getDetailYaml = (uuid: string, name: string) => {
    setDetailVisible(true);
    setDetailLoading(true);
    setDetailData({ name });
    getYaml({ uuid })
      .then((res: any) => {
        setDetailData(res);
        setDetailLoading(false);
        setDetailError(false);
      })
      .catch(() => {
        setDetailLoading(false);
        setDetailError(false);
      });
  };

  // model delete
  const deleteDetailYaml = (uuid: string) => {
    setDeleteLoading(true);
    deleteYaml({ uuid })
      .then(() => {
        setDeleteVisible(false);
        setDeleteLoading(false);
        notification.success({ description: formatMessage({ id: 'dbl_model_management_delete_success' }) });
        const isReset = pageInfo?.pageIndex > 1 && total - 1 === (pageInfo?.pageIndex - 1) * pageInfo?.pageSize;
        void refreshTableAndFilters({
          pageInfo: isReset ? { ...pageInfo, pageIndex: 1 } : pageInfo,
        });
        // 更新缓存操作
        const cacheManager = createCacheManager(); // 缓存管理器
        const data: any = cacheManager?.load();
        if (data?.gameCode === gameCode && data?.userId === userId && data?.modelList?.find((item: any) => item?.uuid === uuid)) {
          const newModelList = data?.modelList?.filter((item: any) => item?.uuid !== uuid) || [];
          if (newModelList.length > 0) {
            const newData = {
              ...(data || {}),
              modelList: newModelList,
            };
            cacheManager?.save(newData);
          } else {
            cacheManager?.clear();
          }
        }
      })
      .catch(() => {
        notification.error({ description: formatMessage({ id: 'dbl_model_management_delete_failed' }) });
        setDeleteVisible(false);
        setDeleteLoading(false);
      });
  };

  // model test
  const testDetailYaml = (uuid: string) => {
    setTestLoading(true);
    testYaml({ items: [{ uuid }] })
      .then(res => {
        const r0 = res?.results?.[0];
        const code = r0?.code;

        if (code === 0) {
          notification.success({
            className: YAML_TEST_RESULT_NOTIFICATION_CLASS,
            description: formatMessage({ id: 'dbl_model_management_test_success' }),
          });
          void refreshTableAndFilters();
          return;
        }

        if (r0) {
          showYamlTestFailureFeedback(formatMessage, {
            kind: 'single',
            msg: r0.msg,
            code: r0.code,
          });
        } else {
          showYamlTestFailureFeedback(formatMessage, { kind: 'single' });
        }
      })
      .catch((err: unknown) => {
        showYamlTestFailureFeedback(formatMessage, { kind: 'request', err });
      })
      .finally(() => {
        setTestLoading(false);
      });
  };

  // model publish
  const publishDetailYaml = (uuid: string) => {
    setPublishLoading(true);
    publishYaml({ items: [{ uuid }] })
      .then(res => {
        const code = res?.results?.[0]?.code;
        if (code === 0) {
          notification.success({ description: formatMessage({ id: 'dbl_model_management_publish_success' }) });
          void refreshTableAndFilters();
        } else {
          notification.error({ description: formatMessage({ id: 'dbl_model_management_publish_failed' }) });
        }
        setPublishLoading(false);
      })
      .catch(() => {
        notification.error({ description: formatMessage({ id: 'dbl_model_management_publish_failed' }) });
        setPublishLoading(false);
      });
  };

  // model unpublish
  const unPublishDetailYaml = (uuid: string) => {
    setUnpublishLoading(true);
    unpublishYaml({ items: [{ uuid }] })
      .then(res => {
        const code = res?.results?.[0]?.code;
        if (code === 0) {
          notification.success({ description: formatMessage({ id: 'dbl_model_management_unpublish_success' }) });
          void refreshTableAndFilters();
        } else {
          notification.error({ description: formatMessage({ id: 'dbl_model_management_unpublish_failed' }) });
        }
        setUnpublishLoading(false);
      })
      .catch(() => {
        notification.error({ description: formatMessage({ id: 'dbl_model_management_unpublish_failed' }) });
        setUnpublishLoading(false);
      });
  };

  // model access
  const handleClickItemAccess = (record: IModelRecord) => {
    const editors: Array<{ userName: string; userId: string; isView: boolean; isDevelop: boolean; }> = [];
    (record?.editable_person || []).forEach((person: IEditablePerson) => {
      if (person.user_id === record.user_id) return;
      const onlyRead = person?.permission?.length === 1 ? person?.permission?.includes('read') : (person?.permission || []).filter((access: string) => access === 'read')?.length === 2;
      editors.push({ userName: person?.user_name, userId: person?.user_id, isView: !!onlyRead, isDevelop: (person?.permission || []).length > 1 });
    });
    setAccessInfo({ id: record.uuid, editors, creatorId: record.user_id || '' });
  };

  const onActionDetail = (record: IModelRecord) => {
    setActionsDetail(record);
  };

  const onPagingChange = (queryPaging: PagingQuery) => {
    void getTableData(queryPaging as unknown as IPageInfo, sort, quickValue).catch(() => {
      notification.warning({
        className: YAML_TEST_RESULT_NOTIFICATION_CLASS,
        description: formatMessage({ id: 'dbl_model_management_list_refresh_failed' }),
      });
    });
  };

  const onSortChange = (value: any[]) => {
    void getTableData(pageInfo, value[0] ?? {}, quickValue).catch(() => {
      notification.warning({
        className: YAML_TEST_RESULT_NOTIFICATION_CLASS,
        description: formatMessage({ id: 'dbl_model_management_list_refresh_failed' }),
      });
    });
  };

  // 排序列定义
  const sortColumns: SortableColumn[] = [
    { key: 'name', prefer: 'asc' },
    { key: 'updated_at', prefer: 'desc' },
  ];
  const loadingState = {
    deleteLoading,
    detailLoading,
    testLoading,
    publishLoading,
    unpublishLoading,
  };
  const columns: TableColumn[] = useMemo(() => getColumns({
    formatMessage,
    quickValue,
    deploymentStageFilterData,
    publishVersionFilterData,
    creatorsFilterData,
    nameMap,
    gameCode,
    loadingState,
    actionsDetail,
    isEn,
    onEdit: (rec) => history.push(`/${SYSTEM_NAME}/${gameCode}/DataManagement/ModelManagementCenter/Edit?uuid=${rec?.uuid}&name=${rec?.name}`),
    onDetail: (uuid, name) => getDetailYaml(uuid, name),
    onAccess: (rec) => handleClickItemAccess(rec),
    onDeleteClick: (rec) => {
      setDeleteInfo(rec);
      setDeleteVisible(true);
    },
    onTest: (uuid) => testDetailYaml(uuid),
    onPublish: (uuid) => publishDetailYaml(uuid),
    onUnpublish: (uuid) => unPublishDetailYaml(uuid),
    onActionDetail: (record) => onActionDetail(record),
    handleOnClickCreateSearch: (key, value) => handleOnClickCreateSearch(key as any, value),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    formatMessage,
    JSON.stringify(actionsDetail),
    JSON.stringify(loadingState),
    JSON.stringify(quickValue),
    JSON.stringify(deploymentStageFilterData),
    JSON.stringify(publishVersionFilterData),
    JSON.stringify(creatorsFilterData),
    JSON.stringify(nameMap),
    gameCode,
  ]);

  return {
    // state
    isEn,
    searchWords,
    searchValue,
    setSearchValue,
    setSearchWords,
    filterData,
    sort,
    quickValue,
    pageInfo,
    records,
    loading,
    total,
    error,
    detailVisible,
    setDetailVisible,
    detailData,
    detailLoading,
    detailError,
    accessInfo,
    setAccessInfo,
    deleteInfo,
    setDeleteInfo,
    deleteVisible,
    setDeleteVisible,
    deleteLoading,
    nameMap,

    // derived
    creatorsFilterData,
    deploymentStageFilterData,
    publishVersionFilterData,

    // handlers
    getTableData,
    getFilterData,
    handleClearSearch,
    handleSearchWithKeyword,
    handleOnClickCreateSearch,
    getDetailYaml,
    deleteDetailYaml,
    testDetailYaml,
    publishDetailYaml,
    unPublishDetailYaml,
    handleClickItemAccess,
    onPagingChange,
    onSortChange,

    // table
    columns,
    sortColumns,
  };
};


